import api from "./api";

/**
 * `/admin/errors` — the lookup for the failed-request record (issue #1191,
 * D234). `GET /api/admin/errors`: the last 14 days of failed requests, each
 * a route template, method, status, a code from a closed list, a time and
 * a request id. Nothing personal is in a row, so nothing personal can be
 * shown here.
 */

/** Plain-language labels for the closed code list (`api/src/lib/errorEvents.ts`). */
export const ERROR_CODE_LABELS = {
  not_authenticated: "Not signed in",
  forbidden: "Not allowed",
  not_found: "Not found",
  invalid_payload: "Invalid request",
  invalid_credentials: "Wrong email or password",
  email_already_registered: "Email already registered",
  password_change_required: "Password change required",
  wrong_password: "Wrong password",
  link_invalid: "Link invalid or expired",
  reauth_failed: "Confirmation failed",
  delete_refused: "Account deletion refused",
  conflict: "Conflict",
  unsupported_content_type: "Unsupported content type",
  payload_too_large: "Request too large",
  method_not_allowed: "Method not allowed",
  rate_limited: "Rate limited",
  voice_error: "Voice dictation",
  usage_error: "Usage record",
  import_refused: "Import refused",
  internal_error: "Server error",
  other: "Other",
};

export const errorCodeLabel = (code) => ERROR_CODE_LABELS[code] ?? "Other";

/** "2026-10-03T01:02:03Z" as a local date and time, or the raw text if it does not parse. */
export function errorTimeLabel(iso) {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? String(iso ?? "") : d.toLocaleString("en-NZ", { dateStyle: "medium", timeStyle: "medium" });
}

/** Only the filters that are set, in the form the API reads them. */
export function errorQuery({ route = "", status = "", code = "" } = {}) {
  const params = new URLSearchParams();
  if (route.trim()) params.set("route", route.trim());
  if (status.trim()) params.set("status", status.trim());
  if (code) params.set("code", code);
  const s = params.toString();
  return s ? `?${s}` : "";
}

export async function fetchAdminErrors(filters) {
  const { data } = await api.get(`/admin/errors${errorQuery(filters)}`);
  return data;
}

/**
 * `POST /api/admin/errors/test-alert` (issue #1341): the live smoke for the
 * 5xx alert. The route throws on purpose, so 500 is the expected answer.
 * No body is sent (axios would add a form content type to `null`, and the CSRF guard answers 415). Every status resolves (it is data here, not an error); only a missing
 * response throws. `requestId` is the `X-Request-Id` of the answer.
 */
export async function sendTestAlert() {
  const res = await api.post("/admin/errors/test-alert", undefined, { validateStatus: () => true });
  return { status: res.status, requestId: res.headers?.["x-request-id"] ?? "" };
}
